import * as React from "react";
import { Button, Gapped, Input, Link, Modal } from "react-ui";
import { useDebounce } from "use-debounce";
import areEqual from "fbjs/lib/areEqual";

import { PicklistDataItem, PicklistQueryResponse } from "../../../../../Engine/Core/Picklists/PicklistTypes";
import { getTotalCountText } from "../../../../../Helpers/textForTotalCount";
import { PicklistItemList, PicklistItemListHandle } from "../PicklistItemList/PicklistItemList";
import { PicklistColumn } from "../PicklistItemList/PicklistColumn";
import { clampUInt } from "../../../../../../../Common/IntRangeUtils";
import { IterUtils } from "../../../../../../../Common/IterableUtils";
import { emptyRef } from "../../../../TypingUtils";
import { useDimensions } from "../../../../FormParts/HiddenPanel/ReactEffects";
import {
    reactUiModalFooterBottomPadding,
    reactUiModalFooterTopPadding,
    reactUiModalHeaderTopPadding,
} from "../../../../Internals/ThemeProvider/ReactUiModalPaddings";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { useCandyTheme } from "../../../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";
import { ValueEditorsFocusManager } from "../../../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { InternalIcon } from "../../../../Icons/Icon";
import { splitValues } from "../../Common/PicklistFilterBuilder";
import {
    NotFoundContainer,
    HeaderContainer,
    Title,
    WIDTH_MODAL,
    WIDTH_CONTROL_IN_HEADER,
    PicklistHeaderWrapper,
} from "../../Common/Styles/PicklistModal.styles";
import { Loader } from "../../Common/Loader/Loader";

import {
    BodyContainer,
    CustomFooterContainer,
    Header,
    HeaderCell,
    HeaderLine,
    HelpIconWrapper,
    NormativeHelpContainer,
    PicklistHint,
    SearchContainer,
    TotalCountContainer,
} from "./PicklistModal.styles";

interface QueryResponse {
    items: PicklistDataItem[];
    suitableCount: number;
}

export interface PicklistModalProps {
    searchByField: string;
    searchFields: { [key: string]: undefined | string };
    query: (value?: string) => Promise<QueryResponse>;
    onClose: () => void;
    title: string;
    placeholder?: string;
    normativeHelp?: [] | [string, string];
    partialHelpTip?: string;
    footer?: React.ReactNode;

    multiple?: boolean;
    columns: PicklistColumn[];
    value: string;
    onChange: (items: PicklistDataItem[]) => void;
    descriptionInModal: string | React.ReactNode;
    isEnableCaseIndependentValidation?: boolean;
    querySelectedValues: (values: string) => Promise<PicklistQueryResponse>;
}

export function PicklistModal(props: PicklistModalProps): JSX.Element {
    const itemList = React.useRef<PicklistItemListHandle>(emptyRef);
    const input = React.useRef<Input>(emptyRef);
    const theme = useCandyTheme();

    const hasHeaders = React.useMemo(() => props.columns.some(x => x.header != undefined), [props.columns]);

    const [list, setList] = React.useState<PicklistDataItem[]>([]);
    const [suitableCount, setSuitableCount] = React.useState(0);
    const [currentValue, setCurrentValue] = React.useState<PicklistDataItem[] | undefined>(undefined);
    const [loading, setLoading] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const [focusedItemIndex, setFocusedItemIndex] = React.useState<number | undefined>(undefined);

    const focusedItem = React.useMemo(() => list[focusedItemIndex ?? -1], [focusedItemIndex, list]);
    const [searchValueToExecuteQuery] = useDebounce(searchValue, 500);
    const textRenderTotalCount = React.useMemo(
        () => getTotalCountText(list.length, suitableCount, searchValueToExecuteQuery !== "", props.partialHelpTip),
        [list, suitableCount, searchValueToExecuteQuery, props.partialHelpTip]
    );

    const [header, headerRect] = useDimensions({ liveMeasure: false }, [textRenderTotalCount, suitableCount, list]);
    const [footer, footerRect] = useDimensions({ liveMeasure: false }, [textRenderTotalCount, suitableCount, list]);

    React.useLayoutEffect(() => input.current?.focus(), []);

    React.useLayoutEffect(() => itemList.current?.adjustScrollToViewFocusedElement(), [focusedItemIndex]);

    useAsyncEffect(
        async signal => {
            setLoading(true);
            const response = await props.query(searchValueToExecuteQuery);
            const hasLimit = response.items.length !== response.suitableCount;
            if (currentValue == undefined) {
                if (!!props.value && props.multiple && hasLimit) {
                    const selectedValues = await props.querySelectedValues(props.value);
                    setCurrentValue(stringValueToCurrentValue(props.value, selectedValues.items));
                } else {
                    setCurrentValue(stringValueToCurrentValue(props.value, response.items));
                }
            }
            if (signal.aborted) {
                return;
            }
            setList(response.items);
            setSuitableCount(response.suitableCount);
            setLoading(false);
        },
        [searchValueToExecuteQuery]
    );

    const stringValueToCurrentValue = (value: string, fullList: PicklistDataItem[]): PicklistDataItem[] => {
        const { searchByField, searchFields } = props;
        if (props.multiple) {
            const multipleValues = splitValues(value);
            if (props.isEnableCaseIndependentValidation) {
                const mappedValuesInLowerCase = multipleValues.map(item => item.toString().toLowerCase());
                const mappedValuesInUpperCase = multipleValues.map(item => item.toString().toUpperCase());
                return fullList.filter(
                    item =>
                        multipleValues.includes(item[searchByField]?.toString() ?? "") ||
                        mappedValuesInLowerCase.includes(item[searchByField]?.toString() ?? "") ||
                        mappedValuesInUpperCase.includes(item[searchByField]?.toString() ?? "")
                );
            }
            return fullList.filter(item => multipleValues.includes(item[searchByField]?.toString() ?? ""));
        } else {
            if (props.isEnableCaseIndependentValidation) {
                return fullList.filter(item =>
                    Object.keys(searchFields).every(
                        field =>
                            item[field] === searchFields[field] ||
                            item[field] === searchFields[field]?.toString().toUpperCase() ||
                            item[field] === searchFields[field]?.toString().toLowerCase()
                    )
                );
            }
            return fullList.filter(item =>
                Object.keys(searchFields).every(field => item[field] === searchFields[field])
            );
        }
    };

    const handleSaveAndCloseClick = () => {
        const { searchByField } = props;
        const selectedItems = IterUtils.sortBy(currentValue ?? [], x => x[searchByField] ?? "");
        props.onChange(selectedItems.length > 0 ? selectedItems : [{ [searchByField]: "" }]);
    };

    const handleBodyKeyEvent = (event: React.KeyboardEvent) => {
        handleKeyEvent(event);
        input.current?.focus();
    };

    const handleKeyEvent = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "Enter":
                selectFocusedItem();
                event.preventDefault();
                break;
            case "ArrowDown":
                setFocusedItemIndex(clampUInt((focusedItemIndex ?? -1) + 1, 0, list.length - 1));
                event.preventDefault();
                break;
            case "End":
                if (event.ctrlKey) {
                    if (list.length > 0) {
                        setFocusedItemIndex(list.length - 1);
                        event.preventDefault();
                    }
                }
                break;
            case "PageDown": {
                let nextIndex = itemList.current?.findMostBottomAndVisibleItemIndex();
                if (nextIndex != undefined) {
                    if (nextIndex === focusedItemIndex) {
                        nextIndex = itemList.current?.findNextPageBottomItemIndex();
                        setFocusedItemIndex(nextIndex);
                    } else {
                        setFocusedItemIndex(nextIndex);
                    }
                    event.preventDefault();
                }
                break;
            }
            case "PageUp": {
                let nextIndex = itemList.current?.findMostTopAndVisibleItemIndex();
                if (nextIndex != undefined) {
                    if (nextIndex === focusedItemIndex) {
                        nextIndex = itemList.current?.findPrevPageTopItemIndex();
                        setFocusedItemIndex(nextIndex);
                    } else {
                        setFocusedItemIndex(nextIndex);
                    }
                    event.preventDefault();
                }
                break;
            }
            case "Home":
                if (event.ctrlKey) {
                    setFocusedItemIndex(0);
                    event.preventDefault();
                }
                break;
            case "ArrowUp":
                setFocusedItemIndex(clampUInt((focusedItemIndex ?? list.length) - 1, 0, list.length - 1));
                event.preventDefault();
                break;
            default:
                break;
        }
    };

    const handleItemListChange = React.useCallback(
        (nextItems: PicklistDataItem[]) => {
            if (props.multiple) {
                input.current?.focus();
            }
            performUpdateCurrentValue(nextItems);
        },
        [props.multiple, props.onChange]
    );

    const handleChangeFocusedItem = React.useCallback(
        (item: undefined | PicklistDataItem) => {
            setFocusedItemIndex(item == undefined ? undefined : list.findIndex(x => areEqual(x, item)));
        },
        [list]
    );

    const selectFocusedItem = (): void => {
        if (focusedItem == undefined) {
            return;
        }
        if (props.multiple) {
            const currentValueOrEmpty = currentValue ?? [];
            const checked = currentValueOrEmpty.some(x => areEqual(x, focusedItem));
            const nextValue = checked
                ? currentValueOrEmpty.filter(x => !areEqual(x, focusedItem))
                : [...currentValueOrEmpty, focusedItem];
            performUpdateCurrentValue(nextValue);
        } else {
            performUpdateCurrentValue([focusedItem]);
            ValueEditorsFocusManager.focusOnNextValueEditor();
        }
    };

    const performUpdateCurrentValue = (nextValue: PicklistDataItem[]) => {
        if (props.multiple) {
            setCurrentValue(nextValue);
        } else {
            props.onChange(nextValue);
        }
    };

    const hasFooter = props.multiple || props.footer != undefined;
    const decreasedPadding = props.footer != undefined && !props.multiple;

    return (
        <Modal ignoreBackgroundClick width={WIDTH_MODAL} onClose={props.onClose}>
            <PicklistHeaderWrapper>
                <Modal.Header>
                    <HeaderContainer ref={header} tabIndex={-1} onKeyDown={handleKeyEvent}>
                        <Title data-tid="Title">{props.title}</Title>
                        {props.normativeHelp != undefined && props.normativeHelp.length > 0 && (
                            <NormativeHelpContainer>
                                <Link href={props.normativeHelp[0]} target="_blank">
                                    <HelpIconWrapper>
                                        <InternalIcon
                                            name="BookClosedHelpIcon"
                                            size={theme.typography.primary.fontSize.getNumber()}
                                        />
                                    </HelpIconWrapper>
                                    {props.normativeHelp[1]}
                                </Link>
                            </NormativeHelpContainer>
                        )}
                        <PicklistHint width={WIDTH_CONTROL_IN_HEADER}>{props.descriptionInModal}</PicklistHint>
                        <SearchContainer textRenderTotalCount={textRenderTotalCount}>
                            <Input
                                data-tid="SearchInput"
                                leftIcon={
                                    <InternalIcon
                                        name="SearchLoupeIcon"
                                        size={theme.typography.primary.fontSize.getNumber()}
                                    />
                                }
                                placeholder={props.placeholder}
                                ref={input}
                                value={searchValue || ""}
                                width={WIDTH_CONTROL_IN_HEADER}
                                onValueChange={setSearchValue}
                            />
                        </SearchContainer>
                        {list.length < suitableCount && (
                            <TotalCountContainer data-tid="Picklist-textRenderTotalCount">
                                {textRenderTotalCount}
                            </TotalCountContainer>
                        )}
                        {hasHeaders && (
                            <Header decreasedPadding={decreasedPadding} checkable={props.multiple ?? false}>
                                {props.columns.map(column => (
                                    <HeaderCell key={column.fieldName} width={column.width}>
                                        {column.header}
                                    </HeaderCell>
                                ))}
                            </Header>
                        )}
                        {hasHeaders && <HeaderLine decreasedPadding={decreasedPadding} width={680} />}
                    </HeaderContainer>
                </Modal.Header>
            </PicklistHeaderWrapper>
            <Modal.Body>
                <BodyContainer tabIndex={-1} onKeyDown={handleBodyKeyEvent}>
                    <Loader active={loading}>
                        {list.length > 0 ? (
                            <PicklistItemList
                                ref={itemList}
                                focusedItem={focusedItem}
                                scrollTopOffset={
                                    (headerRect?.height ?? 0) + reactUiModalHeaderTopPadding + (!hasHeaders ? 22 : 0)
                                }
                                scrollBottomOffset={
                                    (footerRect?.height ?? 0) +
                                    (hasFooter ? reactUiModalFooterTopPadding + reactUiModalFooterBottomPadding : 0)
                                }
                                columns={props.columns}
                                multiple={props.multiple ?? false}
                                value={currentValue ?? []}
                                list={list}
                                onChange={handleItemListChange}
                                onChangeFocusedItem={handleChangeFocusedItem}
                            />
                        ) : !loading ? (
                            <NotFoundContainer>{`Ничего не найдено по запросу "${searchValueToExecuteQuery}"`}</NotFoundContainer>
                        ) : (
                            <div />
                        )}
                    </Loader>
                </BodyContainer>
            </Modal.Body>
            {hasFooter && (
                <Modal.Footer panel={props.multiple}>
                    <div ref={footer}>
                        {props.footer && (
                            <CustomFooterContainer increaseBottomMargin={props.multiple ?? false}>
                                {props.footer}
                            </CustomFooterContainer>
                        )}
                        {props.multiple && (
                            <Gapped gap={8}>
                                <Button
                                    use="primary"
                                    size={"medium"}
                                    onClick={handleSaveAndCloseClick}
                                    data-tid="PicklistSaveButton">
                                    Сохранить и закрыть
                                </Button>
                                <Button size={"medium"} onClick={props.onClose} data-tid="PicklistCancelButton">
                                    Не сохранять
                                </Button>
                            </Gapped>
                        )}
                    </div>
                </Modal.Footer>
            )}
        </Modal>
    );
}
